// Import heic2any with dynamic import to avoid TypeScript issues
let heic2anyLib: any = null;

const loadHeic2Any = async () => {
  if (!heic2anyLib) {
    heic2anyLib = await import('heic2any');
  }
  return heic2anyLib.default || heic2anyLib;
};

/**
 * Converts HEIC image to JPEG format
 * @param file - The HEIC file to convert
 * @param quality - JPEG quality (0-1, default 0.8)
 * @returns Promise<File> - The converted JPEG file
 */
export const convertHeicToJpeg = async (file: File, quality: number = 0.8): Promise<File> => {
  try {
    console.log('🔄 Converting HEIC to JPEG:', file.name);
    
    const heic2any = await loadHeic2Any();
    const convertedBlobs = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: quality
    });

    // heic2any returns an array of blobs, take the first one
    const jpegBlob = Array.isArray(convertedBlobs) ? convertedBlobs[0] : convertedBlobs;
    
    // Create a new File object with JPEG MIME type
    const jpegFile = new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpg'), {
      type: 'image/jpeg',
      lastModified: file.lastModified
    });

    console.log('✅ HEIC conversion successful:', jpegFile.name, jpegFile.size, 'bytes');
    return jpegFile;
  } catch (error) {
    console.error('❌ HEIC conversion failed:', error);
    throw new Error(`Failed to convert HEIC image: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Checks if a file is a HEIC image
 * @param file - The file to check
 * @returns boolean - True if the file is HEIC
 */
export const isHeicFile = (file: File): boolean => {
  const heicMimeTypes = [
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence'
  ];
  
  const heicExtensions = ['.heic', '.heif'];
  
  // Check MIME type
  if (heicMimeTypes.includes(file.type.toLowerCase())) {
    return true;
  }
  
  // Check file extension as fallback
  const fileName = file.name.toLowerCase();
  return heicExtensions.some(ext => fileName.endsWith(ext));
};

/**
 * Validates if a file is a supported image type (including HEIC)
 * @param file - The file to validate
 * @returns boolean - True if the file is a supported image type
 */
export const isValidImageFile = (file: File): boolean => {
  const supportedMimeTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence'
  ];
  
  const supportedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
  
  // Check MIME type
  if (supportedMimeTypes.includes(file.type.toLowerCase())) {
    return true;
  }
  
  // Check file extension as fallback
  const fileName = file.name.toLowerCase();
  return supportedExtensions.some(ext => fileName.endsWith(ext));
};
